import { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useDeleteExpense } from "@/hooks/useExpenses";
import { useToast } from "@/hooks/use-toast";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";

import TabErrorBanner from "@/components/v2/molecules/admin/shared/TabErrorBanner";
import ExpenseFilters from "@/components/v2/molecules/admin/tabs/AdminExpensesTab/ExpenseFilters";
import ExpensesTabSkeleton from "@/components/v2/molecules/admin/tabs/AdminExpensesTab/ExpensesTabSkeleton";
import ReceiptPreviewDialog from "@/components/v2/molecules/admin/tabs/shared/ReceiptPreviewDialog";
import DeleteDialog from "@/components/v2/molecules/admin/tabs/shared/DeleteDialog";
import TablePagination from "@/components/v2/molecules/admin/shared/TablePagination";
import { useExpenseFilters } from "@/hooks/admin/tabs/useExpenseFilters";
import useRolePermissions from "@/hooks/admin/shared/useRolePermissions";
import useTransactionModals from "@/hooks/admin/shared/useTransactionModals";
import type { Expense } from "@/hooks/useExpenses";
import { Plus } from "lucide-react";
import ExpenseFormModal from "../AdminExpensesTab/ExpenseFormModal";
import BulkEditExpenseModal from "../AdminExpensesTab/BulkEditExpenseModal";
import ExpensesTable from "../AdminExpensesTab/ExpensesTable";
import { useShiftSelect } from "@/hooks/useShiftSelect";

interface SelectionState {
  count: number;
  total: number;
  onDelete: () => void;
  onEdit?: () => void;
  isDeleting: boolean;
}

export interface TransactionsExpensesViewRef {
  downloadCSV: () => void;
  createNew: () => void;
  hasData: boolean;
}

interface TransactionsExpensesViewProps {
  onSelectionChange?: (selection: SelectionState | null) => void;
  showOnlySelected?: boolean;
}

const TransactionsExpensesView = forwardRef<TransactionsExpensesViewRef, TransactionsExpensesViewProps>(
  ({ onSelectionChange, showOnlySelected }, ref) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const { canCreate, canDelete, canEdit } = useRolePermissions();
  const { toast } = useToast();

  const deleteExpenseMutation = useDeleteExpense();

  const {
    itemToEdit: expenseToEdit,
    isModalOpen: isExpenseModalOpen,
    isDeleteDialogOpen: isDeleteExpenseDialogOpen,
    setIsDeleteDialogOpen: setIsDeleteExpenseDialogOpen,
    isReceiptModalOpen,
    receiptUrlToPreview,
    handleNew: handleNewExpense,
    handleEdit: handleEditExpense,
    handleModalClose: handleExpenseModalClose,
    handleDelete: handleDeleteExpense,
    confirmDelete: confirmDeleteExpense,
    handleViewReceipt,
    handleReceiptModalClose,
  } = useTransactionModals<Expense>({
    onDelete: async (id) => deleteExpenseMutation.mutateAsync(id),
    withReceipt: true,
  });

  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const {
    filteredExpenses,
    categories,
    properties,
    tags,
    isLoading: isLoadingExpenses,
    error: errorExpenses,
    refetch: refetchExpenses,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,
    searchTerm,
    selectedCategory,
    selectedTag,
    selectedOrganizations,
    selectedProperties,
    dateFrom,
    dateTo,
    hasActiveFilters,
    setSearchTerm,
    setSelectedCategory,
    setSelectedTag,
    setSelectedOrganizations,
    setSelectedProperties,
    setDateFrom,
    setDateTo,
    clearFilters,
  } = useExpenseFilters();

  const {
    selectedItems: selectedExpenses,
    selectedItemsData,
    handleSelect: handleSelectExpense,
    handleSelectAll,
    clearSelection,
    getSelectedTotal,
  } = useShiftSelect({ items: filteredExpenses });

  const handleEditSelected = () => {
    setIsBulkEditModalOpen(true);
  };

  // Use stored data for accurate total calculation across pages
  const selectedExpensesTotal = useMemo(() => {
    return getSelectedTotal('amount');
  }, [getSelectedTotal]);

  // Filter to show only selected items when filter is active
  const displayedExpenses = useMemo(() => {
    if (showOnlySelected && selectedExpenses.length > 0) {
      // Use stored data instead of filtering current page
      return Array.from(selectedItemsData.values());
    }
    return filteredExpenses;
  }, [filteredExpenses, selectedExpenses, selectedItemsData, showOnlySelected]);

  const handleDeleteSelected = () => {
    setIsBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(
        selectedExpenses.map(expenseId => 
          deleteExpenseMutation.mutateAsync(expenseId)
        )
      );

      toast({
        title: "Éxito",
        description: `Se eliminaron ${selectedExpenses.length} gasto(s) correctamente`,
      });

      clearSelection();
      setIsBulkDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting selected expenses:", error);
      toast({
        title: "Error",
        description: "Error al eliminar los gastos seleccionados",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadCSV = () => {
    const expensesToExport = selectedExpenses.length > 0
      ? filteredExpenses.filter(expense => selectedExpenses.includes(expense.id))
      : filteredExpenses;

    const csvHeaders = [
      'Fecha',
      'Propiedad',
      'Categoría',
      'Subcategoría',
      'Descripción',
      'Monto',
      'Estatus',
      'Forma de Pago',
    ];

    const csvData = expensesToExport.map((expense) => [
      expense.expense_date,
      expense.properties?.short_name || 'N/A',
      expense.category?.name || 'N/A',
      expense.subcategory?.name || 'N/A',
      expense.description || 'N/A',
      expense.amount.toString(),
      expense.status,
      expense.payment_method,
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `gastos_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'CSV descargado',
      description: `Se han exportado ${expensesToExport.length} gastos`,
    });
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    downloadCSV: handleDownloadCSV,
    createNew: handleNewExpense,
    hasData: filteredExpenses.length > 0,
  }));

  // Notify parent of selection changes
  useEffect(() => {
    if (selectedExpenses.length > 0) {
      onSelectionChange?.({
        count: selectedExpenses.length,
        total: selectedExpensesTotal,
        onDelete: handleDeleteSelected,
        onEdit: canEdit ? handleEditSelected : undefined,
        isDeleting,
      });
    } else {
      onSelectionChange?.(null);
    }
  }, [selectedExpenses.length, selectedExpensesTotal, isDeleting, canEdit, onSelectionChange]);

  if (isLoadingExpenses) {
    return <ExpensesTabSkeleton />;
  }

  if (errorExpenses) {
    return (
      <TabErrorBanner
        title="Error al cargar gastos"
        message="No se pudieron cargar los gastos. Por favor, intenta nuevamente."
        onRetry={() => refetchExpenses()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <ExpenseFilters
        categories={categories}
        tags={tags}
        searchTerm={searchTerm}
        dateFrom={dateFrom}
        dateTo={dateTo}
        hasActiveFilters={hasActiveFilters}
        properties={properties}
        organizations={organizations}
        contracts={contracts}
        selectedCategory={selectedCategory}
        selectedTag={selectedTag}
        selectedOrganizations={selectedOrganizations}
        selectedProperties={selectedProperties}
        clearFilters={clearFilters}
        setSearchTerm={setSearchTerm}
        setDateFrom={setDateFrom}
        setDateTo={setDateTo}
        setSelectedCategory={setSelectedCategory}
        setSelectedTag={setSelectedTag}
        setSelectedOrganizations={setSelectedOrganizations}
        setSelectedProperties={setSelectedProperties}
      />

      {filteredExpenses.length === 0 ? (
        <div className="bg-muted/50 border border-border rounded-lg p-8 text-center flex flex-col items-center">
          <h3 className="text-foreground font-medium mb-2">No hay gastos</h3>
          <p className="text-muted-foreground text-sm mb-4">
            No se encontraron gastos que coincidan con los filtros aplicados.
          </p>
          {canCreate && (
            <Button
              onClick={handleNewExpense}
              className="bg-brand-primary hover:bg-brand-primary-hover flex items-center gap-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Gasto
            </Button>
          )}
        </div>
      ) : (
        <>
          <ExpensesTable
            expenses={displayedExpenses}
            onViewReceipt={handleViewReceipt}
            onEdit={handleEditExpense}
            onDelete={handleDeleteExpense}
            onSelectExpense={handleSelectExpense}
            selectedExpenses={selectedExpenses}
            onSelectAll={handleSelectAll}
            canEdit={canEdit}
            canDelete={canDelete}
          />

          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={goToPage}
            onPageSizeChange={changePageSize}
            pageSizeOptions={[100, 500]}
          />
        </>
      )}

      <ExpenseFormModal
        isOpen={isExpenseModalOpen}
        onClose={handleExpenseModalClose}
        expense={expenseToEdit}
        defaultPropertyId={selectedProperties[0]}
      />

      <BulkEditExpenseModal
        isOpen={isBulkEditModalOpen}
        onClose={() => {
          setIsBulkEditModalOpen(false);
          clearSelection();
        }}
        selectedExpenseIds={selectedExpenses}
      />

      <ReceiptPreviewDialog
        title="Comprobante de Gasto"
        isOpen={isReceiptModalOpen}
        onClose={handleReceiptModalClose}
        receiptUrl={receiptUrlToPreview}
      />

      <DeleteDialog
        title="¿Estás seguro?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el registro de gasto."
        open={isDeleteExpenseDialogOpen}
        onOpenChange={setIsDeleteExpenseDialogOpen}
        onConfirm={confirmDeleteExpense}
      />

      <DeleteDialog
        title={`¿Eliminar ${selectedExpenses.length} gasto(s)?`}
        description={`Esta acción no se puede deshacer. Se eliminarán permanentemente ${selectedExpenses.length} registro(s) de gasto.`}
        open={isBulkDeleteDialogOpen}
        onOpenChange={setIsBulkDeleteDialogOpen}
        onConfirm={confirmBulkDelete}
        isLoading={isDeleting}
      />
    </div>
  );
});

TransactionsExpensesView.displayName = 'TransactionsExpensesView';

export default TransactionsExpensesView;